import type { SxProps, Theme } from '@mui/material/styles';
import { APP_FONT_SIZE, APP_LINE_HEIGHT } from './typography';

export const DATA_TABLE_TOOLBAR_SX: SxProps<Theme> = {
  px: { xs: 2, sm: 2.5 },
  py: 2,
  gap: 1.25,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  bgcolor: 'background.paper',
  borderBottom: 1,
  borderColor: 'divider',
};

export const DATA_TABLE_TOOLBAR_FILTERS_ROW_SX: SxProps<Theme> = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'flex-start',
  gap: 1.5,
  width: '100%',
};

export const DATA_TABLE_TOOLBAR_FIELD_SX: SxProps<Theme> = {
  flex: '1 1 220px',
  minWidth: 180,
  maxWidth: { xs: '100%', sm: 280 },
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    bgcolor: 'background.default',
  },
  '& .MuiInputBase-input': {
    fontSize: APP_FONT_SIZE.sm,
    lineHeight: APP_LINE_HEIGHT.normal,
  },
  '& .MuiInputLabel-root': {
    fontSize: APP_FONT_SIZE.sm,
  },
  '& .MuiFormHelperText-root': {
    mx: 0,
    mt: 0.5,
    lineHeight: 1.4,
  },
};

export const dataTableSx = (minWidth?: number): SxProps<Theme> => ({
  ...(minWidth != null ? { minWidth } : {}),
  '& .MuiTableCell-head': {
    fontWeight: 600,
    fontSize: '0.9375rem',
    lineHeight: APP_LINE_HEIGHT.normal,
    color: 'text.secondary',
    bgcolor: 'background.paper',
    borderBottom: (t) => `1px solid ${t.palette.divider}`,
    py: 1.25,
    px: 2,
    verticalAlign: 'middle',
  },
  '& .MuiTableCell-body': {
    fontSize: APP_FONT_SIZE.sm,
    lineHeight: APP_LINE_HEIGHT.normal,
    py: 1.25,
    px: 2,
    verticalAlign: 'middle',
    color: 'text.primary',
    borderBottom: (t) => `1px solid ${t.palette.divider}`,
  },
  '& .MuiTableBody-root .MuiTableRow-root:hover .MuiTableCell-body': {
    bgcolor: 'action.hover',
  },
  '& .MuiTableRow-root:last-child .MuiTableCell-body': {
    borderBottom: 0,
  },
  '& .MuiTableSortLabel-root': {
    color: 'inherit',
    '&:hover': { color: 'text.primary' },
    '&.Mui-active': {
      color: 'primary.main',
      '& .MuiTableSortLabel-icon': { color: 'primary.main' },
    },
  },
});

export const DATA_TABLE_CONTAINER_SX: SxProps<Theme> = {
  overflowX: 'auto',
  bgcolor: 'background.paper',
};

export const DATA_TABLE_FOOTER_SX: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'flex-end',
  borderTop: 1,
  borderColor: 'divider',
  px: { xs: 2, sm: 3 },
  py: 2,
  bgcolor: 'background.paper',
};

export const DATA_TABLE_ACTION_BUTTON_SX: SxProps<Theme> = {
  borderRadius: '10px',
  textTransform: 'none',
  fontWeight: 600,
  px: 1.75,
  py: 0.625,
};

export const DATA_TABLE_PAGINATION_SX: SxProps<Theme> = {
  borderTop: 1,
  borderColor: 'divider',
  px: { xs: 1, sm: 2 },
  bgcolor: 'background.paper',
};

export const DATA_TABLE_SAVE_BUTTON_SX: SxProps<Theme> = {
  borderRadius: '12px',
  textTransform: 'none',
  fontWeight: 600,
  px: 3,
  py: 1.25,
};

/** Small inline icon beside metric/card titles (no tinted badge box). */
export const inlineMetricIconSx: SxProps<Theme> = {
  color: 'text.secondary',
  display: 'inline-flex',
  flexShrink: 0,
  alignItems: 'center',
  '& svg': { fontSize: 20 },
};
